import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  Typography,
} from '@mui/material';
import { ChangeEvent, FC } from 'react';
import {
  ArticleFormat,
  BrandKey,
  UpsertArticleMutation_mutation_upsertArticle_Article_qualifiers_ArticleQualifier,
  GetQualifiersQuery_query_qualifiers_Qualifier,
  QualifierOrderingInput,
  QualifierStatus,
} from '../../../__generated__/queries-web';
import { useQualifiers } from './getQualifers.web.graphql';
import { useStyles } from './styles';

interface QualifiersProps {
  brandKey: BrandKey;
  qualifiers: UpsertArticleMutation_mutation_upsertArticle_Article_qualifiers_ArticleQualifier[];
  onChange: (
    updatedQualifiers: UpsertArticleMutation_mutation_upsertArticle_Article_qualifiers_ArticleQualifier[],
  ) => void;
  articleFormat: ArticleFormat;
  label?: string;
}

export const Qualifiers: FC<QualifiersProps> = ({
  brandKey,
  qualifiers,
  onChange,
  articleFormat,
  label,
}) => {
  const classes = useStyles();
  const { data } = useQualifiers({
    first: 100,
    where: { brandKey, format: articleFormat },
    orderBy: [QualifierOrderingInput.title_ASC],
  });

  const publishedQualifiers = data?.qualifiers?.filter(
    (qualifier: GetQualifiersQuery_query_qualifiers_Qualifier) =>
      qualifier.status !== QualifierStatus.Draft,
  );

  const isChecked = (id: string) =>
    qualifiers.some(({ qualifier }) => qualifier.id === id);

  const handleChange = (
    selectedQualifier: GetQualifiersQuery_query_qualifiers_Qualifier,
  ) => (event: ChangeEvent<HTMLInputElement>) => {
    let updatedQualifiers = [];
    if (!event.target.checked) {
      updatedQualifiers = qualifiers.filter(
        ({ qualifier }) => qualifier.id !== selectedQualifier.id,
      );
    } else {
      updatedQualifiers = [
        ...qualifiers,
        ({
          qualifier: {
            ...selectedQualifier,
          },
          __typename: 'ArticleQualifier',
        } as unknown) as UpsertArticleMutation_mutation_upsertArticle_Article_qualifiers_ArticleQualifier,
      ];
    }
    onChange(updatedQualifiers);
  };

  if (!publishedQualifiers?.length) return null;

  return (
    <FormControl className={classes.formSuggest} variant="standard">
      {label ? (
        <Typography className={classes.subTitle} component="h3">
          {label}{' '}
          <span>
            ({publishedQualifiers?.filter(({ id }) => isChecked(id)).length})
          </span>
        </Typography>
      ) : (
        <InputLabel shrink>Qualifiers :</InputLabel>
      )}
      <FormGroup>
        {publishedQualifiers?.map(
          (qualifier: GetQualifiersQuery_query_qualifiers_Qualifier) => (
            <FormControlLabel
              key={qualifier.id}
              control={
                <Checkbox
                  checked={isChecked(qualifier.id)}
                  className={classes.checkbox}
                  onChange={handleChange(qualifier)}
                  value={qualifier.id}
                />
              }
              label={qualifier.title}
            />
          ),
        )}
      </FormGroup>
    </FormControl>
  );
};
